import { canonicalEventType, eventTypeLabel, EVENT_TYPE_OPTIONS } from "./event-types";
import { PhoneError, parsePhone } from "./phone";
import type { CrmSnapshot, DaySlot, EventChanges } from "./tasting";
import { cellText, GuestCountError, matchVenueId, parseGuestCount, workbookFrom } from "./workbook";

export const WEBSITE_INQUIRY_SUBJECT = "Αίτημα από ιστοσελίδα evnt47.com";

/** How many lead-card steps the header shows. */
export const LEAD_STEP_COUNT = 4;

export function inboxSubject(channel: string | null | undefined, subject: string | null | undefined): string {
  const text = (subject ?? "").trim();
  if (text) return text;
  if (channel === "form") return WEBSITE_INQUIRY_SUBJECT;
  return "Χωρίς θέμα";
}

export type InterestedParty = {
  given_name: string;
  family_name: string;
  phone: string | null;
  email: string | null;
  relation: string | null;
  notes: string | null;
  /** Whether this person came to the tasting. Unchecked until Step 2 marks it. */
  came: boolean;
};

/** Roles that usually come to the tasting. Anything else is typed under Άλλο. */
export const RELATION_OPTIONS = [
  { value: "bride", label: "Νύφη" },
  { value: "groom", label: "Γαμπρός" },
  { value: "bride_mother", label: "Μαμά νύφης" },
  { value: "bride_father", label: "Μπαμπάς νύφης" },
  { value: "groom_mother", label: "Μαμά γαμπρού" },
  { value: "groom_father", label: "Μπαμπάς γαμπρού" },
  { value: "best_man", label: "Κουμπάρος" },
  { value: "best_woman", label: "Κουμπάρα" },
] as const;

export function relationSelection(value: string | null | undefined): { choice: string; other: string } {
  const text = (value ?? "").trim();
  if (!text) return { choice: "", other: "" };
  const known = RELATION_OPTIONS.find((option) => option.label.toLocaleLowerCase("el") === text.toLocaleLowerCase("el"));
  if (known) return { choice: known.value, other: "" };
  return { choice: "other", other: text };
}

export function relationStored(choice: string): string | null {
  if (!choice || choice === "other") return null;
  return RELATION_OPTIONS.find((option) => option.value === choice)?.label ?? null;
}

export type Attendance = "came" | "missed" | "postponed";

export type LeadStep = 1 | 2 | 3 | 4;

/**
 * Step 3 «Δέσμευση» is the verbal-commitment date already stored as workbook.commitment
 * («Ημ. προφορικής δέσμευσης» on the staff card). It is not a ReservationCoordinator hold:
 * a hold locks a hall for a time window and would block availability.
 */
export type LeadAgreement = {
  commitment: string | null;
  contract_date: string | null;
  deposit_date: string | null;
  event_date: string | null;
  event_type: string | null;
  venue_id: string | null;
  file_code: string | null;
  guests: number | null;
};

export type LeadExtras = {
  parties?: InterestedParty[];
  step?: LeadStep;
  attended?: Attendance | null;
  intake_id?: string | null;
  agreement?: LeadAgreement;
};

const AGREEMENT_TYPES = new Set<string>([...EVENT_TYPE_OPTIONS.map(([key]) => key), "other"]);

export function emptyAgreement(): LeadAgreement {
  return {
    commitment: null,
    contract_date: null,
    deposit_date: null,
    event_date: null,
    event_type: null,
    venue_id: null,
    file_code: null,
    guests: null,
  };
}

export function cardStepOf(cardStep: number | null | undefined, legacy: number | null | undefined): LeadStep {
  if (cardStep === 1 || cardStep === 2 || cardStep === 3 || cardStep === 4) return cardStep;
  return legacy === 2 ? 2 : 1;
}

/**
 * The step an event without a client card sits at, read from its sales stage:
 * proposal work is the agreement step, a booked or completed event is the contract step.
 */
export function stageCardStep(salesStage: string | null | undefined): LeadStep {
  switch (salesStage) {
    case "proposal_preparation":
    case "proposal_sent":
    case "negotiation":
      return 3;
    case "accepted_pending_booking":
    case "confirmed":
    case "completed":
      return 4;
    default:
      return 1;
  }
}

/** The card's own step when it has one, otherwise the step its sales stage maps to. */
export function eventCardStep(cardStep: number | null | undefined, salesStage: string | null | undefined): LeadStep {
  if (cardStep === 1 || cardStep === 2 || cardStep === 3 || cardStep === 4) return cardStep;
  return stageCardStep(salesStage);
}

/** Where an event's client card opens: the lead-step page at the event's current step. */
export function eventCardHref(eventId: string): string {
  return `/events/${encodeURIComponent(eventId)}/card`;
}

/** The client path: 1/4 πρώτη επαφή, then 2/4, 3/4, 4/4, then a completed event is an old customer. */
export function clientPhaseLabel(step: number | null | undefined, salesStage: string | null | undefined): string {
  if (salesStage === "completed") return "παλιός πελάτης";
  const card = eventCardStep(step, salesStage);
  return card === 1 ? "1/4 · Πρώτη επαφή" : `${card}/4`;
}

/** The original step check only allows 1 or 2, so later steps live in card_step. */
export function storedLeadStep(step: LeadStep): { card_step: LeadStep; step: 1 | 2 } {
  const legacy: 1 | 2 = step === 1 ? 1 : 2;
  return { card_step: step, step: legacy };
}

/** The original attended check cannot be widened, so postponed is stored beside it. */
export function storedAttendance(value: Attendance | null): { attendance: Attendance | null; attended: "came" | "missed" | null } {
  if (value === "postponed") return { attendance: "postponed", attended: null };
  return { attendance: value, attended: value };
}

export function attendanceOf(attendance: string | null | undefined, legacy: string | null | undefined): Attendance | null {
  for (const value of [attendance, legacy]) {
    if (value === "came" || value === "missed" || value === "postponed") return value;
  }
  return null;
}

export class InquiryInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InquiryInputError";
  }
}

export function emptyParty(): InterestedParty {
  return { given_name: "", family_name: "", phone: null, email: null, relation: null, notes: null, came: false };
}

export function parseLeadExtras(body: Record<string, unknown>): LeadExtras {
  const extras: LeadExtras = {};
  if ("parties" in body) extras.parties = parseParties(body.parties);
  if ("step" in body) {
    if (body.step !== 1 && body.step !== 2 && body.step !== 3 && body.step !== 4) throw new InquiryInputError("Το βήμα είναι 1, 2, 3 ή 4.");
    extras.step = body.step;
  }
  if ("agreement" in body) extras.agreement = parseAgreement(body.agreement);
  if ("attended" in body) {
    if (body.attended == null || body.attended === "") extras.attended = null;
    else if (body.attended === "came" || body.attended === "missed" || body.attended === "postponed") extras.attended = body.attended;
    else throw new InquiryInputError("Η παρουσία είναι Ήρθε, Δεν ήρθε ή Αναβολή.");
  }
  if ("intake_id" in body) {
    if (body.intake_id == null || body.intake_id === "") extras.intake_id = null;
    else if (typeof body.intake_id === "string" && /^[A-Za-z0-9_-]{1,80}$/.test(body.intake_id)) extras.intake_id = body.intake_id;
    else throw new InquiryInputError("Το αίτημα δεν είναι έγκυρο.");
  }
  return extras;
}

export function parseStoredParties(value: string | null | undefined): InterestedParty[] {
  if (!value) return [];
  try {
    return parseParties(JSON.parse(value) as unknown, "read");
  } catch (error) {
    if (error instanceof InquiryInputError) return [];
    return [];
  }
}

function partyCame(value: unknown, mode: "write" | "read"): boolean {
  if (value == null || value === "") return false;
  if (typeof value === "boolean") return value;
  if (mode === "read") return false;
  throw new InquiryInputError("Η παρουσία του ενδιαφερόμενου είναι ναι ή όχι.");
}

function parseParties(value: unknown, mode: "write" | "read" = "write"): InterestedParty[] {
  if (!Array.isArray(value)) throw new InquiryInputError("Οι ενδιαφερόμενοι δεν είναι έγκυροι.");
  if (value.length > 8) throw new InquiryInputError("Οι ενδιαφερόμενοι είναι έως 8.");
  const parties: InterestedParty[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) throw new InquiryInputError("Οι ενδιαφερόμενοι δεν είναι έγκυροι.");
    const record = entry as Record<string, unknown>;
    const party: InterestedParty = {
      given_name: text(record.given_name, 120),
      family_name: text(record.family_name, 120),
      phone: mode === "read" ? text(record.phone, 40) || null : partyPhone(record.phone),
      email: text(record.email, 200).toLowerCase() || null,
      relation: text(record.relation, 80) || null,
      notes: text(record.notes, 4000) || null,
      came: partyCame(record.came, mode),
    };
    if (!party.given_name && !party.family_name && !party.phone && !party.email && !party.relation && !party.notes) continue;
    parties.push(party);
  }
  return parties;
}

export function agreementFromSources(source: {
  workbook?: Record<string, string>;
  startDate?: string | null;
  eventType?: string | null;
  venueId?: string | null;
  adults?: number | null;
  interestDate?: string | null;
  occasion?: string | null;
  guestCount?: number | null;
  venues?: { id: string; name: string }[];
  venueHint?: string | null;
}): LeadAgreement {
  const book = source.workbook ?? {};
  const guests = guestsCell(book.agreed_guests) ?? wholeGuests(source.adults) ?? wholeGuests(source.guestCount);
  return {
    commitment: isoCell("commitment", book.commitment),
    contract_date: isoCell("contract_date", book.contract_date),
    deposit_date: isoCell("deposit_date", book.deposit_date),
    event_date: isoCell("agreed_date", book.agreed_date) ?? isoDate(source.startDate) ?? isoDate(source.interestDate),
    event_type: agreementType(source.eventType) ?? agreementType(source.occasion),
    venue_id: resolveVenueId(source.venueId, source.venues, source.venueHint),
    file_code: (book.file_code ?? "").trim() || null,
    guests,
  };
}

/** Fills Step 3 from earlier steps without overwriting a value the user already changed. */
export function followAgreement(current: LeadAgreement, previousDerived: LeadAgreement, derived: LeadAgreement): LeadAgreement {
  const next: LeadAgreement = { ...current };
  const keys = Object.keys(derived) as (keyof LeadAgreement)[];
  for (const key of keys) {
    if (current[key] == null || current[key] === previousDerived[key]) assignAgreement(next, key, derived[key]);
  }
  return next;
}

function assignAgreement<K extends keyof LeadAgreement>(target: LeadAgreement, key: K, value: LeadAgreement[K]): void {
  target[key] = value;
}

/**
 * Writes Step 3 onto the workbook cells the staff card and the overview band already use.
 * Blank inputs clear those cells. Blank inputs do not wipe event columns filled earlier.
 * Projected workbook keys (visit date and the like) stay in place.
 */
export function applyAgreement(changes: EventChanges, snapshot: CrmSnapshot, agreement: LeadAgreement, venues: { id: string; name: string }[]): void {
  const book: Record<string, string> = { ...(changes.workbook ?? snapshot.workbook) };
  setCell(book, "commitment", agreement.commitment);
  setCell(book, "contract_date", agreement.contract_date);
  setCell(book, "deposit_date", agreement.deposit_date);
  setCell(book, "agreed_date", agreement.event_date);
  setCell(book, "file_code", agreement.file_code);
  setCell(book, "agreed_guests", agreement.guests == null ? null : String(agreement.guests));
  setCell(book, "agreed_type", agreement.event_type ? eventTypeLabel(agreement.event_type) || agreement.event_type : null);
  const venue = agreement.venue_id ? venues.find((item) => item.id === agreement.venue_id) : undefined;
  setCell(book, "agreed_venue", venue?.name ?? null);
  const next = workbookFrom(book);
  if (JSON.stringify(next) !== JSON.stringify(workbookFrom(snapshot.workbook))) changes.workbook = next;

  if (agreement.event_date && (agreement.event_date !== snapshot.start_date || ("start_date" in changes && changes.start_date !== agreement.event_date))) {
    changes.start_date = agreement.event_date;
    changes.date_precision = "exact";
  }
  if (agreement.event_type && (agreement.event_type !== snapshot.event_type || ("event_type" in changes && changes.event_type !== agreement.event_type))) {
    changes.event_type = agreement.event_type;
  }
  if (venue && (agreement.venue_id !== snapshot.venue_id || ("venue_id" in changes && changes.venue_id !== agreement.venue_id))) {
    changes.venue_id = agreement.venue_id;
  }
  if (agreement.guests != null && (agreement.guests !== snapshot.adults || ("adults" in changes && changes.adults !== agreement.guests))) {
    changes.adults = agreement.guests;
    changes.guests_estimated = 1;
  }
}

function parseAgreement(value: unknown): LeadAgreement {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new InquiryInputError("Η συμφωνία δεν είναι έγκυρη.");
  const record = value as Record<string, unknown>;
  let guests: number | null;
  try {
    guests = parseGuestCount(record.guests, 1);
  } catch (error) {
    if (!(error instanceof GuestCountError)) throw error;
    throw new InquiryInputError("Τα άτομα γράφονται ως ακέραιος αριθμός.");
  }
  const eventType = record.event_type == null || record.event_type === "" ? null : text(record.event_type, 80);
  if (eventType && !AGREEMENT_TYPES.has(eventType)) throw new InquiryInputError("Ο τύπος εκδήλωσης δεν είναι έγκυρος.");
  const venueId = record.venue_id == null || record.venue_id === "" ? null : text(record.venue_id, 80);
  if (venueId && !/^[A-Za-z0-9_-]{1,80}$/.test(venueId)) throw new InquiryInputError("Ο χώρος δεν είναι έγκυρος.");
  return {
    commitment: parseDate(record.commitment, "Η δέσμευση"),
    contract_date: parseDate(record.contract_date, "Η ημερομηνία συμφωνητικού"),
    deposit_date: parseDate(record.deposit_date, "Η ημερομηνία προκαταβολής"),
    event_date: parseDate(record.event_date, "Η ημερομηνία εκδήλωσης"),
    event_type: eventType,
    venue_id: venueId,
    file_code: text(record.file_code, 80) || null,
    guests,
  };
}

function parseDate(value: unknown, label: string): string | null {
  if (value == null || value === "") return null;
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new InquiryInputError(`${label} δεν είναι έγκυρη ημερομηνία.`);
  return value;
}

function setCell(book: Record<string, string>, key: string, value: string | null): void {
  if (value) book[key] = value;
  else delete book[key];
}

function isoCell(key: string, value: string | null | undefined): string | null {
  return isoDate(cellText(key, value));
}

function isoDate(value: string | null | undefined): string | null {
  const textValue = (value ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(textValue) ? textValue : null;
}

function agreementType(value: string | null | undefined): string | null {
  const code = canonicalEventType(value);
  return code && AGREEMENT_TYPES.has(code) ? code : null;
}

function wholeGuests(value: number | null | undefined): number | null {
  if (value == null || !Number.isInteger(value) || value < 1 || value > 100000) return null;
  return value;
}

function guestsCell(value: string | undefined): number | null {
  try {
    return parseGuestCount(value, 1);
  } catch (error) {
    if (!(error instanceof GuestCountError)) throw error;
    return null;
  }
}

function resolveVenueId(venueId: string | null | undefined, venues: { id: string; name: string }[] | undefined, hint: string | null | undefined): string | null {
  const id = (venueId ?? "").trim();
  if (!venues) return id || null;
  if (id && venues.some((venue) => venue.id === id)) return id;
  return hint ? matchVenueId(venues, hint) : null;
}

function partyPhone(value: unknown): string | null {
  try {
    return parsePhone(value);
  } catch (error) {
    if (error instanceof PhoneError) throw new InquiryInputError(error.message);
    throw error;
  }
}

function text(value: unknown, max: number): string {
  if (value == null) return "";
  if (typeof value !== "string") throw new InquiryInputError("Ένα πεδίο κειμένου δεν είναι έγκυρο.");
  return value.trim().slice(0, max);
}

/**
 * The optional μεσημέρι/βράδυ choice on the public inquiry form. Empty means "not said";
 * anything other than midday|evening is rejected.
 */
export function inquirySlot(value: unknown): DaySlot | null {
  if (value == null || value === "") return null;
  if (value === "midday" || value === "evening") return value;
  throw new InquiryInputError("Η ώρα είναι μεσημέρι ή βράδυ.");
}

/** Workbook service_time text for a slot; slotFromText reads it back for the clash warning and the lead card. */
export function slotServiceTime(slot: DaySlot): string {
  return slot === "midday" ? "Μεσημέρι" : "Βράδυ";
}
